import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Store } from '@/redux';
import {
  describeTurn,
  SUIT_GLYPH,
  TurnDescription,
} from '@/screens/guest/lib/turn';
import { SocketConnectionState } from '../../use-websocket';
import '@/styles/sunday-table.css';

export const useTurnDescription = (): TurnDescription => {
  const { players, gameInfo } = useSelector(
    (state: Store) => state.inPlayReducer
  );
  const userID = useSelector((state: Store) => state.userReducer.id);

  return useMemo(
    () =>
      describeTurn({
        userID,
        room: gameInfo?.room,
        inPlayCards: gameInfo?.inPlayCards,
        userDeck: gameInfo?.userDeck,
        players,
      }),
    [userID, gameInfo, players]
  );
};

export interface TurnBannerProps {
  compact?: boolean;
  connectionState?: SocketConnectionState;
  onReconnect?: () => void;
}

/**
 * One-line "what should I do now" strip: whose turn, which suit leads and
 * whether the local player must follow it. Announced politely to screen
 * readers whenever it changes.
 */
export const TurnBanner: FC<TurnBannerProps> = ({
  compact = false,
  connectionState = 'open',
  onReconnect,
}) => {
  const turn = useTurnDescription();
  const offline = connectionState !== 'open';

  return (
    <div
      className={`st-turn st-turn--${turn.tone} ${compact ? 'st-turn--compact' : ''}`}
      data-testid="turn-banner"
    >
      {turn.leadSuit && (
        <span
          className={`st-turn__suit st-suit--${turn.leadSuit}`}
          aria-hidden="true"
        >
          {SUIT_GLYPH[turn.leadSuit]}
        </span>
      )}
      <div className="st-turn__text" role="status" aria-live="polite">
        <strong className="st-turn__title">{turn.title}</strong>
        {turn.detail && <span className="st-turn__detail">{turn.detail}</span>}
      </div>
      {offline && (
        <div className="st-turn__conn" role="alert">
          <span className="st-dot st-dot--warn" aria-hidden="true" />
          {connectionState === 'failed' ? (
            <>
              <span>Disconnected</span>
              {onReconnect && (
                <button
                  type="button"
                  className="st-btn st-btn--small st-btn--ghost"
                  onClick={onReconnect}
                >
                  Reconnect
                </button>
              )}
            </>
          ) : (
            <span>
              {connectionState === 'connecting'
                ? 'Connecting…'
                : 'Reconnecting…'}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
